import React from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { paymentResponsibilityOptions } from '../constants';
import AddressForm from './AddressForm';
import type { SubscriberFormProps } from '../types';

const genderOptions = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'U', label: 'Unknown' },
];

const SubscriberForm: React.FC<SubscriberFormProps> = ({
  subscriber,
  onChange,
}) => {
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>Member ID</label>
          <Input
            value={subscriber?.memberId || ''}
            onChange={e => onChange('memberId', e.target.value)}
            placeholder='Member ID'
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Payment Responsibility Level <span className='text-red-500'>*</span>
          </label>
          <Select
            value={subscriber?.paymentResponsibilityLevelCode || ''}
            onValueChange={value =>
              onChange('paymentResponsibilityLevelCode', value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select responsibility level' />
            </SelectTrigger>
            <SelectContent>
              {paymentResponsibilityOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>
            First Name <span className='text-red-500'>*</span>
          </label>
          <Input
            value={subscriber?.firstName || ''}
            onChange={e => onChange('firstName', e.target.value)}
            placeholder='First name'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Last Name <span className='text-red-500'>*</span>
          </label>
          <Input
            value={subscriber?.lastName || ''}
            onChange={e => onChange('lastName', e.target.value)}
            placeholder='Last name'
            required
          />
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>Middle Name</label>
          <Input
            value={subscriber?.middleName || ''}
            onChange={e => onChange('middleName', e.target.value)}
            placeholder='Middle name or initial'
            maxLength={25}
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>Suffix</label>
          <Input
            value={subscriber?.suffix || ''}
            onChange={e => onChange('suffix', e.target.value)}
            placeholder='Jr., III, etc.'
            maxLength={10}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <div>
          <label className='block text-sm font-medium mb-1'>Gender</label>
          <Select
            value={subscriber?.gender || ''}
            onValueChange={value => onChange('gender', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select gender' />
            </SelectTrigger>
            <SelectContent>
              {genderOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>
            Date of Birth
          </label>
          <Input
            value={subscriber?.dateOfBirth || ''}
            onChange={e => onChange('dateOfBirth', e.target.value)}
            placeholder='YYYYMMDD'
            maxLength={8}
          />
        </div>

        <div>
          <label className='block text-sm font-medium mb-1'>Group Number</label>
          <Input
            value={subscriber?.groupNumber || ''}
            onChange={e => onChange('groupNumber', e.target.value)}
            placeholder='Group number'
          />
        </div>
      </div>

      <div>
        <label className='block text-sm font-medium mb-1'>SSN</label>
        <Input
          value={subscriber?.ssn || ''}
          onChange={e => onChange('ssn', e.target.value)}
          placeholder='Social Security Number (9 digits)'
          maxLength={9}
        />
      </div>

      {/* Address Section */}
      <div className='space-y-2'>
        <label className='block text-sm font-medium'>Subscriber Address</label>
        {subscriber?.address ? (
          <AddressForm
            address={subscriber.address}
            onChange={(field, value) =>
              onChange('address', { ...subscriber.address, [field]: value })
            }
          />
        ) : (
          <div className='text-sm text-gray-500 p-4 border border-dashed border-gray-300 rounded'>
            No address provided
            <button
              type='button'
              onClick={() =>
                onChange('address', {
                  address1: '',
                  city: '',
                  state: '',
                  postalCode: '',
                })
              }
              className='ml-2 text-blue-600 hover:text-blue-800 underline'
            >
              Add Address
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriberForm;
